import { createContext, useContext, useState, useEffect } from 'react';

const CRMAuthContext = createContext(null);

export const useCRMAuth = () => {
  const context = useContext(CRMAuthContext);
  if (!context) {
    throw new Error('useCRMAuth must be used within CRMAuthProvider');
  }
  return context;
};

export const CRMAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('crm_token'));

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API}/api/crm/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        logout();
      }
    } catch (err) {
      console.error('Auth error:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/crm/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('crm_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    // Return false if still loading or user not set
    if (loading || !user) return false;
    return user.role === 'super_admin' || user.role === 'admin';
  };

  const isSuperAdmin = () => {
    if (loading || !user) return false;
    return user.role === 'super_admin';
  };

  const canViewPricing = () => {
    if (loading || !user) return false;
    return user.role === 'super_admin' || user.role === 'admin';
  };

  return (
    <CRMAuthContext.Provider value={{
      user,
      setUser,
      token,
      setToken,
      loading,
      login,
      logout,
      isAdmin,
      isSuperAdmin,
      canViewPricing,
      isAuthenticated: !!user
    }}>
      {children}
    </CRMAuthContext.Provider>
  );
};

export default CRMAuthProvider;
